<template>
  <div
    id="ai-assistant-fab"
    :class="[fabStateClass]"
    @mouseover="onHover"
    @mouseleave="onLeave"
    @click="handleClick"
    :aria-expanded="opened"
  >
    <!-- Remote recording state: single row — pulse icon | text (2 lines) | waveform | ghost button -->
    <template v-if="isRemoteRecording">
      <span class="fab-icon fab-icon-pulse">
        <w-icon name="smart" size="large" color="#00897B" />
      </span>
      <div class="fab-remote-text">
        <span class="fab-remote-line">Gravação em andamento</span>
      </div>
      <div class="fab-waveform">
        <span class="bar"></span>
        <span class="bar"></span>
        <span class="bar"></span>
        <span class="bar"></span>
        <span class="bar"></span>
      </div>
      <button class="fab-go-btn" @click.stop="emit('finalize-recording')">Ir para gravação</button>
    </template>

    <!-- Default state -->
    <template v-else>
      <span :class="['fab-icon', { 'fab-icon-bg': isExpanded, 'fab-icon-processing': isProcessingMinimized }]">
        <w-spinner v-if="isTransitioning" size="large" />
        <w-spinner v-if="isProcessingMinimized" class="processing-spinner" size="large" />
        <w-icon name="smart" :size="iconSize" :color="iconColor" />
      </span>
      <div v-if="isExpanded" class="fab-texts">
        <w-text class="fab-title" type="sectionHeading">{{ fabTitle }}</w-text>
        <w-text class="fab-subtitle" type="bodyEmphasis">{{ fabSubtitle }}</w-text>
      </div>
    </template>
  </div>
</template>

<style lang="css">
.w-tooltip-popout {
  text-align: center;
}
</style>

<style scoped lang="scss">
@import "style-variables.scss";

#ai-assistant-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 50px;
  box-shadow: none;
  transition: width 0.3s, background 0.3s, color 0.3s, padding 0.3s;
  overflow: hidden;
  height: 64px;
  width: 64px;
  z-index: 10;

  &.fab-default {
    width: 64px;
    height: 64px;
    background: $primary-color;
    color: $primary-color-inverted;
    justify-content: center;
    .fab-icon {
      margin: 0 auto;
      background: none;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      min-width: 48px;
      min-height: 48px;
      color: $primary-color-inverted;
    }
    .fab-texts {
      display: none;
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.2s 0.1s;
    }
  }

  &.fab-hover {
    width: 210px;
    background: $primary-color-hover;
    color: $primary-color-inverted;
    padding: 0 18px 0 8px;
    .fab-icon {
      margin-right: 12px;
      color: $primary-color;
      opacity: 1;
    }
    .fab-texts {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      white-space: nowrap;
    }

    &.fab-processing {
      width: 230px;
    }
  }

  &.fab-active {
    width: 280px;

    .fab-texts {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      white-space: nowrap;
    }
  }

  // Banner style - activated by GrowthBook feature flag
  &.fab-banner {
    width: 40%;
    min-width: 600px;
    height: 80px;
    background: linear-gradient(135deg, #4dd0be 0%, #3bb4a4 100%);
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 137, 123, 0.3);
    display: flex;
    align-items: center;
    justify-content: space-between;

    // Centralizar horizontalmente na tela
    left: 50%;
    transform: translateX(-50%);
    right: auto;

    .fab-icon {
      margin-right: 16px;
      background: rgba(255, 255, 255, 0.2);
      width: 48px;
      height: 48px;
      min-width: 48px;
      min-height: 48px;
      color: white;
    }

    .fab-texts {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      flex: 1;
      white-space: normal;

      .fab-title {
        font-size: 16px;
        font-weight: 600;
        line-height: 1.3;
        margin-bottom: 4px;
        color: white;
      }

      .fab-subtitle {
        font-size: 13px;
        font-weight: 400;
        opacity: 0.95;
        color: white;
        line-height: 1.2;
      }
    }

    // Animation pulse effect for banner
    animation: bannerPulse 2s ease-in-out infinite;
  }

  // Remote recording state — single horizontal row
  &.fab-recording-remote {
    width: auto;
    height: 68px;
    background: $primary-color;
    padding: 0 16px 0 10px;
    gap: 12px;
    overflow: visible;
    box-shadow: 0 4px 20px rgba(0, 137, 123, 0.35);

    .fab-icon-pulse {
      position: relative;
      background: rgba(230, 250, 245, 0.95);
      flex-shrink: 0;

      &::before,
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 2px solid rgba(0, 137, 123, 0.55);
        animation: iconPulseRing 2.2s ease-out infinite;
        pointer-events: none;
      }

      &::after {
        animation-delay: 1.1s;
      }
    }

    .fab-remote-text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 2px;
      flex-shrink: 0;
    }

    .fab-remote-line {
      font-size: 12px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.92);
      white-space: nowrap;
      line-height: 1.35;
    }

    .fab-waveform {
      display: flex;
      align-items: center;
      gap: 3px;
      height: 20px;
      flex-shrink: 0;

      .bar {
        width: 3px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.75);
        animation: waveformBounce 1.4s ease-in-out infinite;

        &:nth-child(1) {
          height: 5px;
          animation-delay: 0s;
        }
        &:nth-child(2) {
          height: 14px;
          animation-delay: 0.18s;
        }
        &:nth-child(3) {
          height: 9px;
          animation-delay: 0.36s;
        }
        &:nth-child(4) {
          height: 14px;
          animation-delay: 0.54s;
        }
        &:nth-child(5) {
          height: 5px;
          animation-delay: 0.72s;
        }
      }
    }

    .fab-go-btn {
      flex-shrink: 0;
      margin-left: 4px;
      background: transparent;
      border: 1.5px solid rgba(255, 255, 255, 0.5);
      color: rgba(255, 255, 255, 0.9);
      border-radius: 20px;
      padding: 7px 14px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;
      letter-spacing: 0.01em;
      transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.14);
        border-color: rgba(255, 255, 255, 0.8);
        color: white;
      }

      &:active {
        background: rgba(255, 255, 255, 0.22);
      }
    }
  }
}

@keyframes bannerPulse {
  0%,
  100% {
    box-shadow: 0 4px 16px rgba(0, 137, 123, 0.3);
  }
  50% {
    box-shadow: 0 6px 20px rgba(0, 137, 123, 0.5);
  }
}

@keyframes iconPulseRing {
  0% {
    transform: scale(1);
    opacity: 0.65;
  }
  100% {
    transform: scale(1.85);
    opacity: 0;
  }
}

@keyframes waveformBounce {
  0%,
  100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.25);
  }
}

.fab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  min-width: 48px;
  min-height: 48px;
  background: none;
  color: $primary-color-inverted;
  position: relative;
}

.fab-icon-bg {
  background: #e6faf5;
}

.fab-icon-processing {
  position: relative;

  .processing-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
}

.fab-texts {
  margin-left: 6px;
  .fab-title {
    margin-bottom: 2px;
    color: $primary-color-inverted;
  }
  .fab-subtitle {
    opacity: 0.85;
    color: $primary-color-inverted;
  }
}
</style>
<script setup lang="ts">
/**
 * AIAssistantFab.vue - Floating Action Button
 *
 * Main entry point for the AI Assistant. Displays as a floating button
 * that expands on hover and triggers the assistant flow on click.
 */
import { ref, computed } from "vue";
import { UserTracking } from "@/utils/UserTracking";

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  isTransitioning: {
    type: Boolean,
    default: false,
  },
  isProcessingMinimized: {
    type: Boolean,
    default: false,
  },
  showAsBanner: {
    type: Boolean,
    default: false,
  },
  isRemoteRecording: {
    type: Boolean,
    default: false,
  },
  token: null,
  userId: null,
  patientId: null,
  licenseId: null,
  bookingId: null,
  pms: null,
  displayMode: String,
});

const emit = defineEmits(["click", "finalize-recording"]);

const isHovered = ref(false);
const hasTrackedHover = ref(false);

const onHover = () => {
  isHovered.value = true;

  if (!hasTrackedHover.value) {
    UserTracking.trackEvent("element_visibility_changed", {
      element: "noa widget",
      action: "show element",
      page_category: "patients",
      page_section: "patient profile",
      page_type: "",
    });
    hasTrackedHover.value = true;
  }
};
const onLeave = () => {
  isHovered.value = false;
  hasTrackedHover.value = false;
};

const handleClick = (event: Event) => {
  console.log("[AIAssistantFab] handleClick called");
  emit("click", event);
};

const fabStateClass = computed(() => {
  // Remote recording takes priority
  if (props.isRemoteRecording) {
    return "fab-recording-remote";
  }

  // Show banner style when showAsBanner is true (GrowthBook feature flag)
  if (props.showAsBanner) {
    return "fab-banner";
  }

  // Normal hover behavior
  if (isHovered.value) {
    const classes = ["fab-hover"];
    if (props.active) classes.push("fab-active");
    if (props.isProcessingMinimized) classes.push("fab-processing");
    return classes.join(" ");
  }

  return props.isProcessingMinimized ? "fab-default fab-processing-collapsed" : "fab-default";
});

const isExpanded = computed(() => isHovered.value || props.showAsBanner);
const isProcessingMinimized = computed(() => props.isProcessingMinimized);
const fabTitle = computed(() => {
  if (props.isTransitioning) return "Processando...";
  if (props.isProcessingMinimized) return "Processando...";
  if (props.showAsBanner) return "Utilize a IA para gerar as anotações para seu prontuário!";
  return props.active ? "Atendimento Iniciado" : "Noa Notes";
});
const fabSubtitle = computed(() => {
  if (props.isTransitioning) return "Clique para ver";
  if (props.isProcessingMinimized) return "Clique para ver";
  if (props.showAsBanner) return "Clique agora e comece a transcrever!";
  return "Anotar com IA";
});
const iconSize = "large";
const iconColor = computed(() => (isExpanded.value ? "#00897B" : "#fff"));
</script>
